import { call, put, select, take, delay, fork } from 'redux-saga/effects';
import { messageToObject, waitForSaga } from '../helpers';
import {
    getWebsocketChannels,
    getWebsocketReadyState,
    getWebsocketSubscriptionById,
    getWebsocketSubscriptionsId
} from '../websocketSelectors';
import {
    websocketChannelClose,
    websocketChannelOpen,
    websocketChannelSend,
    websocketClearSubscriptionById,
    websocketCloseSubscriptionById,
    websocketEventClose,
    websocketEventMessage,
    websocketEventOpen,
    websocketOpenSubscriptionById,
    websocketSaveSubscriptionById,
    websocketSetSubscriptionByIdState,
} from '../websocketReducer';

import { IWebsocketSubscriptionById } from '../types/websocketSubscribersReducer.t';
import _ from 'lodash';
import { getIsAppVisibility } from '../../../app/appSelectors';

interface IWebsocketMessage {
    [name: string]: string | string[] | number | boolean | IWebsocketMessage | IWebsocketMessage[] | undefined;
}
export function* sendMessageSaga(
    subscribeMessage: IWebsocketMessage,
    payload: IWebsocketSubscriptionById,
) {
    if (subscribeMessage) {
        const { channel } = payload;
        try {
            const message = JSON.stringify(subscribeMessage);
            yield put(websocketChannelSend({
                channel,
                message,
                messageObject: subscribeMessage,
            }));
            return subscribeMessage;

        } catch (e) {
            console.error('websocketSendMessage', e);
        }
    }
}

export function* waitForMessageWasRepliedSaga({ requestId } = { requestId: undefined }, channel, retry = 100) {
    if (requestId) {
        let messageSended = false;
        let count = 0;
        do {
            const sendedAction = yield take(websocketEventMessage);
            const messageChannel = sendedAction?.payload?.channel;
            if (messageChannel === channel) {
                const eventMessage = messageToObject(sendedAction?.payload?.event) as { inReplyTo?: string };
                if (eventMessage?.inReplyTo === requestId) {
                    messageSended = true;
                    return eventMessage;
                }
            }
            count++;
        } while (!messageSended && count < retry);
        return messageSended;
    } else {
        return true;
    }
}

export function* onOpenSubscriptionByIdSaga(action: ReturnType<typeof websocketOpenSubscriptionById>) {
    const { channel, id, open } = action.payload;
    // есть автоинициализация при открытии канала, эта для уже открытого канала (для переподписки к примеру)
    const websocketChannels: ReturnType<typeof getWebsocketChannels> = yield select(getWebsocketChannels);
    if (websocketChannels[channel]?.readyState !== WebSocket.OPEN) {
        // канал не открыт, сохраняем , вызвваем открытие и завершаем работу ибо подпишется при открытии канала
        yield put(websocketSaveSubscriptionById(action.payload));
        yield put(websocketChannelOpen({ channel }));
        return;
    }

    const subscribesForCheck: ReturnType<typeof getWebsocketSubscriptionsId> = yield select(
        getWebsocketSubscriptionsId,
    );
    const subscribesChannelForCheck = subscribesForCheck[channel];
    if (!subscribesChannelForCheck || !subscribesChannelForCheck[id]) {
        yield put(websocketSaveSubscriptionById(action.payload));
        yield call(waitForSaga, (state) => {
            const subscribe = getWebsocketSubscriptionById(channel, id)(state);
            return Boolean(subscribe);
        });
    } else {
        const subscribe = subscribesChannelForCheck[id];
        if (subscribe.state === 'opening' || subscribe.state === 'open') {
            // если подписка уже есть, проверяем что бы не было дубликатов
            const right = subscribe.prev;
            const left = action.payload.prev;
            if (left !== undefined && _.isEqual(left, right)) {
                return;
            }
        }
    }

    const subscribes: ReturnType<typeof getWebsocketSubscriptionsId> = yield select(getWebsocketSubscriptionsId);
    const subscribesChannel = subscribes[channel];
    if (subscribesChannel) {
        const subscribe = subscribesChannel[id];
        if (subscribe && subscribe.close) {
            if (subscribe.state === 'opening') {
                yield call(waitForSaga, (state) => {
                    const subscribe = getWebsocketSubscriptionById(channel, id)(state);
                    return subscribe?.state !== 'opening';
                });
            }
            // если есть подписка, закрываем ее
            const subscribeCloseMessage  = yield select(subscribe.close);
            if (subscribe.state === 'open' && subscribeCloseMessage) {
                yield put(websocketSetSubscriptionByIdState({ id, channel, state: 'closing' }));
                yield call(sendMessageSaga, subscribeCloseMessage, subscribe);
                const flag = yield call(waitForMessageWasRepliedSaga, subscribeCloseMessage, channel);
                if (flag) {
                    yield put(websocketSetSubscriptionByIdState({ id, channel, state: 'close' }));
                }
            }
            if (subscribe.state === 'closing') {
                yield call(waitForSaga, (state) => {
                    const subscribe = getWebsocketSubscriptionById(channel, id)(state);
                    return subscribe?.state !== 'closing';
                });
            }
        }
    }

    try {
        if (open) {
            const subscribeOpenMessage = yield select(open);
            if (subscribeOpenMessage) {
                yield put(websocketSaveSubscriptionById(action.payload));
                yield call(waitForSaga, (state) => {
                    const subscribe = getWebsocketSubscriptionById(channel, id)(state);
                    return Boolean(subscribe);
                });
                yield put(websocketSetSubscriptionByIdState({ id, channel, state: 'opening' }));
                yield call(sendMessageSaga, subscribeOpenMessage, action.payload);
                yield put(websocketSetSubscriptionByIdState({ id, channel, state: 'open' }));
            }
        }
    } catch (e) {
        console.error('onOpenSubscriptionByIdSaga', e);
    }
}

export function* processSubscriptionByIdSaga(action: ReturnType<typeof websocketEventMessage>) {
    const { channel, event } = action.payload;
    const subscribes: ReturnType<typeof getWebsocketSubscriptionsId> = yield select(getWebsocketSubscriptionsId);
    const subscribesChannel = subscribes[channel];
    try {
        const message = messageToObject(event) as { inReplyTo?: string, type: string };
        const id = message?.inReplyTo || message?.type;
        if (subscribesChannel && subscribesChannel[id] && subscribesChannel[id].process) {
            yield call(subscribesChannel[id].process, message, channel);
        }
    } catch (e) {
        console.error('processSubscriptionByIdSaga', e);
    }
}

export function* onCloseSubscriptionByIdSaga(action: ReturnType<typeof websocketCloseSubscriptionById>) {
    const { channel, id } = action.payload;

    const subscribes: ReturnType<typeof getWebsocketSubscriptionsId> = yield select(getWebsocketSubscriptionsId);
    const subscribesChannel = subscribes[channel];
    if (!subscribesChannel) {
        // channel already closed
        return;
    }
    const subscribe = subscribesChannel?.[id];
    if (subscribe && subscribe.close) {
        if (subscribe.state === 'opening') {
            yield call(waitForSaga, (state) => {
                const subscribe = getWebsocketSubscriptionById(channel, id)(state);
                return subscribe?.state !== 'opening';
            });
        }
        const subscribeCloseMessage  = yield select(subscribe.close);
        if (subscribe.state === 'open' && subscribeCloseMessage) {
            yield put(websocketSetSubscriptionByIdState({ id, channel, state: 'closing' }));
            yield call(sendMessageSaga, subscribeCloseMessage, subscribesChannel[id]);
            yield call(waitForMessageWasRepliedSaga, subscribeCloseMessage, channel);
            yield put(websocketSetSubscriptionByIdState({ id, channel, state: 'close' }));
        }
    }
    const updatedSubscribes: ReturnType<typeof getWebsocketSubscriptionsId> = yield select(getWebsocketSubscriptionsId);
    const updatedSubscribesChannel = updatedSubscribes[channel];
    if (Object.keys(updatedSubscribesChannel).length < 2) {
        // если подписок больше нет, закрываем канал
        yield put(websocketChannelClose({ channel }));
    }
    // удаляем подписку
    yield put(websocketClearSubscriptionById({ id, channel }));
}

function* handleOpenSubscriptionOnWebsocketOpen({ subscribe }: { subscribe: IWebsocketSubscriptionById }) {
    const open = subscribe?.open;
    const openMessage = open ? yield select(open) : null;
    if (openMessage) {
        yield put(
            websocketSetSubscriptionByIdState({
                id: subscribe.id,
                channel: subscribe.channel,
                state: 'opening',
            }),
        );
        yield call(sendMessageSaga, openMessage, subscribe);
        yield put(
            websocketSetSubscriptionByIdState({
                id: subscribe.id,
                channel: subscribe.channel,
                state: 'open',
            }),
        );
    }
}

// при открытии канала отправляем сообщения на открытие подписок
export function* onWebsocketOpenSaga(action: ReturnType<typeof websocketEventOpen>) {
    const { channel } = action.payload;
    const subscribesById: ReturnType<typeof getWebsocketSubscriptionsId> = yield select(getWebsocketSubscriptionsId);
    const subscribesByIdChannel = subscribesById[channel];
    if (subscribesByIdChannel) {
        const subscribesByIdChannelKeys = Object.keys(subscribesByIdChannel);
        for (let i = 0; i < subscribesByIdChannelKeys.length; i++) {
            const subscribe = subscribesByIdChannel[subscribesByIdChannelKeys[i]];
            yield fork(handleOpenSubscriptionOnWebsocketOpen, { subscribe });
        }
    }
}

// при закрытии канала помечаем подписки как закрытые
export function* onWebsocketCloseSaga(action: ReturnType<typeof websocketEventClose>) {
    const { channel } = action.payload;
    const subscribesById: ReturnType<typeof getWebsocketSubscriptionsId> = yield select(getWebsocketSubscriptionsId);
    const subscribesByIdChannel = subscribesById[channel];
    if (subscribesByIdChannel) {
        const subscribesByIdChannelKeys = Object.keys(subscribesByIdChannel);
        for (let i = 0; i < subscribesByIdChannelKeys.length; i++) {
            const subscribe = subscribesByIdChannel[subscribesByIdChannelKeys[i]];
            const id = subscribe?.id;
            if (id) {
                yield put(
                    websocketSetSubscriptionByIdState({
                        id,
                        channel,
                        state: 'close',
                    }),
                );
            }
        }
    }
}


// чистим все подписки при логауте
export function* onLogoutStartSaga() {
    const websocketSubscribers = yield select(getWebsocketSubscriptionsId);
    const channels = Object.keys(websocketSubscribers);
    for (let i = 0; i < channels.length; i++) {
        const channel = channels[i];
        const channelSubscribers = Object.keys(websocketSubscribers[channel])?.length || 0;
        for (let j = 0; j < channelSubscribers; j++) {
            const id = Object.keys(websocketSubscribers[channel])[j];
            yield put(websocketCloseSubscriptionById({ channel, id }));
        }
    }
}

// при сворачивании приложения закрываем все каналы через 10 секунд
// при разворачивании открываем все каналы
// и идет автоподписка при открытиии канала в onWebsocketOpenSaga
export function* onVisibilityChangeSaga(action: ReturnType<typeof websocketEventClose>) {
    const isAppVisibility = yield select(getIsAppVisibility);
    if (isAppVisibility) {
        const websocketSubscribers = yield select(getWebsocketSubscriptionsId);
        const channels = Object.keys(websocketSubscribers);
        const readyStates = yield select(getWebsocketReadyState);
        for (let i = 0; i < channels.length; i++) {
            const channel = channels[i];
            const channelSubscribers = Object.keys(websocketSubscribers[channel])?.length || 0;
            if (channelSubscribers > 0) {
                if (readyStates[channel] === undefined || readyStates[channel] === WebSocket.CLOSED || readyStates[channel] === WebSocket.CLOSING) {
                    yield put(websocketChannelOpen({ channel }));
                }
            }
        }
    } else {
        yield delay(10000);
        const isAppVisibility = yield select(getIsAppVisibility);
        if (!isAppVisibility) {
            const websocketChannels = yield select(getWebsocketChannels);
            const channels = Object.keys(websocketChannels);
            for (let i = 0; i < channels.length; i++) {
                const channel = channels[i];
                const websocket = websocketChannels[channel];
                if (websocket.readyState === WebSocket.OPEN) {
                    yield put(websocketChannelClose({ channel }));
                }
            }
        }
    }
}
